import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';

export function Select({
  value,
  onValueChange,
  children,
  disabled,
  placeholder,
  ariaLabel,
  portalContainer,
}: {
  value: string;
  onValueChange: (v: string) => void;
  children: ReactNode;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  // Portals the dropdown content into this element instead of document.body -
  // needed when the trigger lives inside a real Fullscreen API element,
  // since content portaled to body is outside the fullscreen top layer and
  // would not be visible there.
  portalContainer?: HTMLElement | null;
}) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger aria-label={ariaLabel} className="flex h-9 items-center gap-1.5 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm text-ink outline-none focus-visible:border-accent disabled:opacity-50">
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon>
          <ChevronDown size={14} className="text-muted" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal container={portalContainer ?? undefined}>
        <SelectPrimitive.Content className="z-50 overflow-hidden rounded-[var(--radius-card)] border border-border bg-surface shadow-md">
          <SelectPrimitive.Viewport className="p-1">{children}</SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

export function SelectItem({ value, children }: { value: string; children: ReactNode }) {
  return (
    <SelectPrimitive.Item
      value={value}
      className="relative flex cursor-pointer select-none items-center px-7 py-2 text-sm text-ink outline-none data-[highlighted]:bg-surface-2"
    >
      <SelectPrimitive.ItemIndicator className="absolute left-2 inline-flex items-center">
        <Check size={13} />
      </SelectPrimitive.ItemIndicator>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}
